"use client";

import { useUser } from "@/hooks/useUser";
import { authenticatedFetch } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { track } from "@masterkey/analytics/client";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  History,
  Wand2,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { AuthRequiredModal } from "../auth/auth-required-modal";
import { ImageComparison } from "../image-comparison";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {} from "../ui/scroll-area";
import {} from "../ui/tabs";

interface ImagePair {
  annotatedImage: string;
  sourceImage: string;
  type: string;
  detail?: string;
  reimaginePrompts?: string[];
}

interface Reimagine {
  id: string;
  original_image_url: string;
  reimagined_image_url: string;
  prompt: string;
  created_at: string;
}

interface PropertyReimagineProps {
  imagePairs?: ImagePair[];
  fallbackImage: string;
  className?: string;
  analysisId?: string;
  isModal?: boolean;
}

const REIMAGINE_RATE_LIMIT_KEY = "reimagine_rate_limit_until";

// Helper to check if rate limit is still active
function isRateLimitActive(): boolean {
  const limitUntil = localStorage.getItem(REIMAGINE_RATE_LIMIT_KEY);
  if (!limitUntil) return false;

  const limitTime = Number.parseInt(limitUntil, 10);
  return !Number.isNaN(limitTime) && limitTime > Date.now();
}

// Helper to set rate limit for 24 hours
function setRateLimit() {
  const tomorrow = Date.now() + 24 * 60 * 60 * 1000; // 24 hours from now
  localStorage.setItem(REIMAGINE_RATE_LIMIT_KEY, tomorrow.toString());
}

const FALLBACK_PROMPTS = [
  "Add a luxury pool and landscaping...",
  "Add an outdoor kitchen and dining area...",
  "Convert to mid-century modern style...",
  "Add floor-to-ceiling windows...",
  "Upgrade to luxury finishes...",
  "Convert to modern farmhouse style...",
] as const;

function useTypingPlaceholder(
  prompts: readonly string[],
  typingSpeed = 50,
  deletingSpeed = 30,
  pauseDuration = 2000,
) {
  const [placeholder, setPlaceholder] = useState("");
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const currentPrompt = prompts[currentPromptIndex];

    if (isTyping) {
      if (placeholder.length < currentPrompt.length) {
        timeout = setTimeout(() => {
          setPlaceholder(currentPrompt.slice(0, placeholder.length + 1));
        }, typingSpeed);
      } else {
        timeout = setTimeout(() => {
          setIsTyping(false);
        }, pauseDuration);
      }
    } else {
      if (placeholder.length > 0) {
        timeout = setTimeout(() => {
          setPlaceholder(placeholder.slice(0, -1));
        }, deletingSpeed);
      } else {
        setCurrentPromptIndex((current) => (current + 1) % prompts.length);
        setIsTyping(true);
      }
    }

    return () => clearTimeout(timeout);
  }, [
    placeholder,
    currentPromptIndex,
    isTyping,
    prompts,
    typingSpeed,
    deletingSpeed,
    pauseDuration,
  ]);

  return placeholder;
}

function PropertyReimagineInternal({
  imagePairs,
  fallbackImage,
  className,
  analysisId,
  isModal = false,
}: PropertyReimagineProps) {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [reimaginedImage, setReimagedImage] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [hasUsedFreeReimagine, setHasUsedFreeReimagine] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showingHistory, setShowingHistory] = useState(false);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [groupedReimagines, setGroupedReimagines] = useState<
    Record<string, Reimagine[]>
  >({});
  const hasHistory = Object.keys(groupedReimagines).length > 0;

  const { user } = useUser();
  const router = useRouter();

  // Get current image pair or fallback
  const currentImage =
    imagePairs && imagePairs.length > 0
      ? imagePairs[selectedImageIndex]
      : {
          annotatedImage: fallbackImage,
          sourceImage: fallbackImage,
          type: "original",
        };

  const placeholderPrompts = currentImage.reimaginePrompts || FALLBACK_PROMPTS;
  const placeholder = useTypingPlaceholder(placeholderPrompts);

  const currentReimagines = groupedReimagines[currentImage.sourceImage] ?? [];

  // Reset selectedHistoryIndex when changing images or when there are no reimagines
  useEffect(() => {
    setSelectedHistoryIndex(0);
  }, [currentImage.sourceImage]);

  // Ensure selectedHistoryIndex is within bounds
  useEffect(() => {
    if (selectedHistoryIndex >= currentReimagines.length) {
      setSelectedHistoryIndex(Math.max(0, currentReimagines.length - 1));
    }
  }, [currentReimagines.length, selectedHistoryIndex]);

  // Fetch reimagine history on mount
  useEffect(() => {
    if (!analysisId) return;

    async function fetchReimagineHistory() {
      setIsLoadingHistory(true);
      try {
        const response = await fetch(
          `/api/properties/reimagine/history?analyzedPropertyId=${analysisId}`,
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Failed to fetch reimagine history");
        }

        setGroupedReimagines(data.reimagines);

        // if there are history images for the currently selected image, set showingHistory to true
        if (data.reimagines[currentImage.sourceImage]?.length > 0) {
          setShowingHistory(true);
        }
      } catch (err) {
        console.error("Failed to fetch reimagine history:", err);
      } finally {
        setIsLoadingHistory(false);
      }
    }

    void fetchReimagineHistory();
  }, [analysisId]);

  // Handle reimagine submission
  const handleReimagine = async () => {
    if (!prompt.trim()) return;

    // Check rate limit before making the request
    if (!user && isRateLimitActive()) {
      setHasUsedFreeReimagine(true);
      return;
    }

    setIsGenerating(true);
    setError(null);
    try {
      const response = await (user ? authenticatedFetch : fetch)(
        "/api/properties/reimagine",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            originalImage: currentImage.sourceImage,
            prompt: prompt.trim(),
            ...(analysisId && { analyzedPropertyId: analysisId }),
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          // Rate limit exceeded - set the rate limit and update UI
          setRateLimit();
          setHasUsedFreeReimagine(true);
          return;
        }
        throw new Error(data.error || "Failed to reimagine property");
      }

      setReimagedImage(data.imageUrl);
      setShowingHistory(false); // Hide history when new image is generated

      // If the user is not authenticated and this was their first successful reimagine
      if (!data.isAuthenticated) {
        setRateLimit();
        setHasUsedFreeReimagine(true);
      }

      // Track successful reimagining
      track({
        event: "property-reimagine-completed",
        source: "property-reimagine",
        properties: {
          prompt: prompt.trim(),
          isAuthenticated: !!user,
          imageType: currentImage.type,
        },
      });

      // Clear the prompt after successful generation
      setPrompt("");
    } catch (error) {
      console.error("Failed to reimagine property:", error);
      setError(
        error instanceof Error ? error.message : "Failed to reimagine property",
      );
      // Track failed reimagining
      track({
        event: "property-reimagine-error",
        source: "property-reimagine",
        properties: {
          prompt: prompt.trim(),
          error: error instanceof Error ? error.message : "Unknown error",
          isAuthenticated: !!user,
          imageType: currentImage.type,
        },
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSignInPrompt = () => {
    track({
      event: "reimagine-login-prompt",
      source: "property-reimagine",
      properties: {
        isLoggedIn: false,
        trigger: hasUsedFreeReimagine ? "free_trial_used" : "rate_limit",
      },
    });
    setShowAuthModal(true);
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header - Only show if not modal */}
      {!isModal && (
        <div className="px-4 md:px-0">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100">
                <Wand2 className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-2xl font-semibold">Reimagine This House</h2>
            </div>
            <p className="text-gray-600">
              What would you change about this home? Describe your vision and
              see it transformed by AI.
            </p>
          </div>
        </div>
      )}

      <div className="relative overflow-hidden rounded-none md:rounded-xl border-x-0 border-b border-t md:border border-gray-100 bg-gradient-to-br from-purple-50/30 via-white to-amber-50/30 px-4 py-6 md:p-6">
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px]" />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.02] to-amber-500/[0.02]" />

        <div className="relative space-y-6">
          {/* Image Selection Carousel - Only show if there are multiple images */}
          {imagePairs && imagePairs.length > 1 && (
            <div className="mb-4">
              <div className="flex gap-2 overflow-x-auto pb-4 px-2 pt-2 snap-x snap-mandatory">
                {imagePairs.map((pair, index) => (
                  <button
                    key={pair.annotatedImage}
                    type="button"
                    onClick={() => {
                      setSelectedImageIndex(index);
                      setReimagedImage(null);
                    }}
                    className={cn(
                      "relative flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden snap-start",
                      "ring-2 ring-offset-2 transition-all duration-200",
                      selectedImageIndex === index
                        ? "ring-amber-500 scale-[1.02]"
                        : "ring-transparent hover:ring-amber-200",
                    )}
                  >
                    <div className="relative w-full h-full">
                      <Image
                        src={pair.annotatedImage}
                        alt={`View ${index + 1}`}
                        fill
                        className="object-cover"
                        sizes="96px"
                      />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="relative w-full">
            {/* Previous Reimagines Button - Only show if there's history and not showing history */}
            {hasHistory &&
              !showingHistory &&
              !isGenerating &&
              currentReimagines.length > 0 && (
                <div
                  className={cn(
                    "absolute right-4  z-10",
                    reimaginedImage ? "top-12" : "top-4",
                  )}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowingHistory(true)}
                    className="bg-black/60 hover:bg-black/80 text-white border-white/20 backdrop-blur-sm"
                  >
                    <History className="h-4 w-4 mr-2" />
                    Previous Reimagines
                    <div className="ml-2 bg-amber-400/20 text-amber-200 text-xs font-semibold px-2 py-0.5 rounded-full border border-amber-400/30">
                      {currentReimagines.length}
                    </div>
                  </Button>
                </div>
              )}

            {isGenerating ? (
              // Loading State with Scanning Animation
              <div
                className="relative w-full overflow-hidden rounded-lg"
                style={{ aspectRatio: "16/9" }}
              >
                <Image
                  src={currentImage.annotatedImage}
                  alt="Original space"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  priority
                />

                {/* Scanning animation overlay */}
                <div className="absolute inset-0">
                  {/* Dark overlay for better contrast */}
                  <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px]" />

                  {/* Scanning point with lines */}
                  <div className="absolute animate-scan-point w-full h-full">
                    {/* Vertical line - spans both up and down from the point */}
                    <div className="absolute w-[2px] h-screen -translate-x-1/2 -translate-y-1/2 top-1/2 bg-gradient-to-b from-transparent via-amber-400 to-transparent" />
                    {/* Horizontal line - spans both left and right from the point */}
                    <div className="absolute h-[2px] w-screen -translate-x-1/2 -translate-y-1/2 left-1/2 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
                    {/* Center point */}
                    <div className="absolute -translate-x-1/2 -translate-y-1/2">
                      <div className="w-4 h-4">
                        <div className="absolute inset-0 rounded-full bg-amber-400/30 animate-ping" />
                        <div className="absolute inset-[25%] rounded-full bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.5)]" />
                      </div>
                    </div>
                  </div>

                  {/* Loading text overlay */}
                  <div className="absolute inset-x-0 bottom-0">
                    <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/90 via-black/70 to-transparent" />
                    <div className="relative px-4 py-3">
                      <p className="text-sm font-semibold text-white">
                        Processing your reimagine...
                      </p>
                      <p className="text-xs text-white/90 mt-0.5">
                        This may take a few moments
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : showingHistory ? (
              <div className="relative">
                {isLoadingHistory ? (
                  <div className="space-y-4 animate-pulse">
                    <div className="h-64 bg-gray-200 rounded-lg" />
                  </div>
                ) : currentReimagines.length > 0 &&
                  currentReimagines[selectedHistoryIndex] ? (
                  <div className="relative">
                    <ImageComparison
                      beforeImage={currentImage.sourceImage}
                      afterImage={
                        currentReimagines[selectedHistoryIndex]
                          .reimagined_image_url
                      }
                      beforeLabel="Original"
                      afterLabel="Reimagined"
                      aspectRatio="16/9"
                      handleColor="#f59e0b"
                      mobileAspectRatio="16/9"
                      animateOnLoad={false}
                    />

                    {/* Navigation Arrows */}
                    <div className="absolute inset-y-0 left-6 right-6 flex items-center justify-between pointer-events-none">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "h-12 w-12 rounded-full bg-black/60 text-white hover:bg-black/80 hover:text-white pointer-events-auto transition-all duration-200",
                          selectedHistoryIndex === 0 &&
                            "opacity-0 pointer-events-none",
                        )}
                        onClick={() =>
                          setSelectedHistoryIndex((prev) => prev - 1)
                        }
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "h-12 w-12 rounded-full bg-black/60 text-white hover:bg-black/80 hover:text-white pointer-events-auto transition-all duration-200",
                          selectedHistoryIndex ===
                            currentReimagines.length - 1 &&
                            "opacity-0 pointer-events-none",
                        )}
                        onClick={() =>
                          setSelectedHistoryIndex((prev) => prev + 1)
                        }
                      >
                        <ChevronRight className="h-6 w-6" />
                      </Button>
                    </div>

                    {/* Prompt Overlay */}
                    <div className="absolute inset-x-0 bottom-0 p-4">
                      <div className="bg-black/60 backdrop-blur-sm rounded-lg p-3 text-white">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Wand2 className="h-4 w-4 text-amber-400" />
                            <span className="text-sm text-amber-400">
                              Prompt
                            </span>
                          </div>
                          <p className="text-xs text-gray-400">
                            {formatDistanceToNow(
                              new Date(
                                currentReimagines[selectedHistoryIndex]
                                  .created_at,
                              ),
                              { addSuffix: true },
                            )}
                          </p>
                        </div>
                        <p className="text-sm mt-1">
                          {currentReimagines[selectedHistoryIndex].prompt}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="relative w-full overflow-hidden rounded-lg"
                    style={{ aspectRatio: "16/9" }}
                  >
                    <Image
                      src={currentImage.annotatedImage}
                      alt="Original space"
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, 50vw"
                      priority
                    />
                    <div className="absolute inset-x-0 bottom-0">
                      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/90 via-black/70 to-transparent" />
                      <div className="relative px-4 py-3">
                        <p className="text-sm font-semibold text-white">
                          Original
                        </p>
                        {currentImage.detail && (
                          <p className="text-xs text-white/90 mt-0.5">
                            {currentImage.detail}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Original/Reimagined Image Display
              <>
                {reimaginedImage ? (
                  <ImageComparison
                    beforeImage={currentImage.sourceImage}
                    afterImage={reimaginedImage}
                    beforeLabel="Original"
                    afterLabel="Reimagined"
                    aspectRatio="16/9"
                    handleColor="#f59e0b"
                    mobileAspectRatio="16/9"
                    animateOnLoad={false}
                  />
                ) : (
                  <div
                    className="relative w-full overflow-hidden rounded-lg"
                    style={{ aspectRatio: "16/9" }}
                  >
                    <Image
                      src={currentImage.annotatedImage}
                      alt="Original space"
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, 50vw"
                      priority
                    />
                    <div className="absolute inset-x-0 bottom-0">
                      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/90 via-black/70 to-transparent" />
                      <div className="relative px-4 py-3">
                        <p className="text-sm font-semibold text-white">
                          Original
                        </p>
                        {currentImage.detail && (
                          <p className="text-xs text-white/90 mt-0.5">
                            {currentImage.detail}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Input Section */}
          <div className="mt-6">
            {error && (
              <p className="text-sm text-red-600 text-center mb-3">{error}</p>
            )}
            <div
              className={cn(
                "flex gap-2",
                isModal ? "flex-col" : "flex-col sm:flex-row",
              )}
            >
              <div className="relative flex-1">
                <Input
                  placeholder={placeholder}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="w-full bg-white/90 border-gray-200/90 placeholder:text-gray-500/90 h-12 px-4 text-base focus-within:ring-amber-200"
                  disabled={!user && hasUsedFreeReimagine}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && prompt.trim() && !isGenerating) {
                      handleReimagine();
                    }
                  }}
                />
              </div>
              {!user && hasUsedFreeReimagine ? (
                <Button
                  onClick={handleSignInPrompt}
                  className={cn(
                    "group relative shrink-0 h-12 text-base px-8 bg-black hover:bg-black/90 transition-all duration-300 overflow-hidden",
                    isModal && "w-full",
                  )}
                >
                  <span className="relative flex items-center justify-center gap-2">
                    <Wand2 className="h-5 w-5" />
                    Sign in for More
                  </span>
                </Button>
              ) : (
                <Button
                  onClick={handleReimagine}
                  disabled={isGenerating || !prompt.trim()}
                  className={cn(
                    "group relative shrink-0 h-12 text-base px-8 bg-black hover:bg-black/90 transition-all duration-300 overflow-hidden",
                    isModal && "w-full",
                  )}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-400/40 to-amber-300/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 to-amber-300/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <span className="relative flex items-center justify-center gap-2 min-w-[140px]">
                    <Wand2 className="h-5 w-5 transition-transform duration-500 group-hover:rotate-12" />
                    {isGenerating ? "Processing..." : "Reimagine"}
                  </span>
                </Button>
              )}
            </div>
            {!isModal && (
              <div className="mt-3 text-center">
                <Link
                  href="/reimagine"
                  className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors group"
                >
                  <span>Want to try with your own image? Click here</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <AuthRequiredModal
        isOpen={showAuthModal}
        onSignedIn={() => {
          setShowAuthModal(false);
          router.refresh();
        }}
        onDismiss={() => setShowAuthModal(false)}
        title="Sign in for Unlimited Reimagines"
        description="Transform this property with unlimited AI-powered reimagining. See how it could look with different styles and upgrades."
        signInButtonText="Sign in to Continue"
      />
    </div>
  );
}

// Wrapper component that handles authentication
export function PropertyReimagine(props: PropertyReimagineProps) {
  return <PropertyReimagineInternal {...props} />;
}
